import http from "./httpservices";
import GetCookieByName from "../hooks/GetCookie";

const baseUrl = process.env.REACT_APP_PAYMENT_PORTAL_BASE_URL;
let financialYear = "All";

const STATUS_MAP = {
  success: "Success",
  failed: "Failed",
  waiting: "Waiting",
  rejected: "Rejected",
  applied: "Applied",
  pending: "Pending"
};

export async function paymentCampaign() {
  try {
    const endpoint = "dashboard/";

    const authCookie = GetCookieByName("auth");
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];

    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      headers: {
        "x-auth-token": token,
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in fetching payment dashboard data");
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
}

export async function latestPaymentCampaign() {
  try {
    const latestCampaign = "campaign/";
    const user = JSON.parse(GetCookieByName("user"));
    const authCookie = GetCookieByName("auth");
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];

    const { data } = await http.post(
      `${baseUrl}${latestCampaign}latest`,
      { id: user._id },
      {
        headers: {
          "x-auth-token": token,
          "x-fy": financialYear,
        },
      },
    );

    return data;
  } catch (error) {
    console.log("Error in fetching latest campaigns");
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
}

export async function FetchVendorCategories() {
  try {
    const url = "company/";
    const { data } = await http.get(`${baseUrl}${url}`, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in fetching Vendor categories");
  }
}

export async function FetchVendors() {
  try {
    const url = "company/";
    const { data } = await http.get(`${baseUrl}${url}`, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in fetching Vendor");
    // Falls back to [] rather than implicitly returning undefined — Vendor.jsx puts this
    // straight into state and reads .length off it unconditionally in vendorView, so any
    // failed request (backend down, expired token, ...) was crashing the whole page instead
    // of just showing an empty vendor list.
    return [];
  }
}

async function FetchVendorSearch(params) {
  try {
    const endpoint = "company/";
    const { data } = await http.post(`${baseUrl}${endpoint}search`, params, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in searching Vendors...");
    throw error;
  }
}

export async function FetchCampaigns() {
  try {
    const endpoint = "campaign/pending";
    const user = JSON.parse(GetCookieByName("user"));
    const authCookie = GetCookieByName("auth");
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];

    const { data } = await http.post(
      `${baseUrl}${endpoint}`,
      { id: user._id },
      {
        headers: {
          "x-auth-token": token,
          "x-fy": financialYear,
        },
      },
    );
    return data;
  } catch (error) {
    console.log("Error in fetching all the pending campaigns..");
  }
}

export async function FetchAllCampaigns() {
  try {
    // Update the endpoint to match what you want
    const endpoint = "payment/dashboard/campaigns/pending";

    const user = JSON.parse(GetCookieByName("user"));
    const authCookie = GetCookieByName("auth");
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth.token;

    // Check if this endpoint expects a GET or POST request
    // Based on the endpoint name, it might be a GET request
    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      params: {
        id: user._id,  // If the endpoint expects userId as query param
      },
      headers: {
        "x-auth-token": token,
        "x-fy": financialYear,
      },
    });

    console.log("Campaign data:", data);
    return data;
  } catch (error) {
    console.error("FetchAllCampaigns error:", error.response?.data || error.message);
    return null;
  }
}

export async function FetchCampaignSearch(params) {
  try {
    const endpoint = "campaign/search";

    const userCookie = GetCookieByName("user");
    const authCookie = GetCookieByName("auth");

    // Add validation
    if (!userCookie || !authCookie) {
      throw new Error("Missing authentication cookies");
    }

    const user = JSON.parse(userCookie);
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];

    // Merge user._id into params if needed
    const requestParams = {
      ...params,
      id: user._id, // Assuming this belongs in the request body
    };

    const { data } = await http.post(
      `${baseUrl}${endpoint}`,
      requestParams, // Only 3 arguments: url, data, config
      {
        headers: {
          "x-auth-token": token,
          "x-fy": params.fy || financialYear,
        },
      },
    );
    return data;
  } catch (error) {
    console.log("Search error in finding campaign", error);
    throw error;
  }
}

// Facet options (categories/platforms/creators/budget range) for the
// Campaigns page filter bar, scoped to the caller's permissions and the
// given Financial Year — kept in sync with whatever FY the user has active
// so every option offered actually has a matching campaign.
export async function FetchCampaignFilters(fy) {
  try {
    const endpoint = "campaign/filters";
    const token = JSON.parse(GetCookieByName("auth"))["token"];

    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      headers: {
        "x-auth-token": token,
        "x-fy": fy || financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in fetching campaign filters", error);
    throw error;
  }
}

// Campaign Groups — a lightweight organizational label over existing
// campaigns (they keep their own budget/payment/POC). Lets a user bundle
// campaigns from different POCs into one named bucket for easier browsing.
function authHeaders(fy) {
  return {
    "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
    "x-fy": fy || financialYear,
  };
}

export async function FetchCampaignGroups(fy) {
  try {
    const { data } = await http.get(`${baseUrl}campaign-groups/`, {
      headers: authHeaders(fy),
    });
    return data;
  } catch (error) {
    console.log("Error in fetching campaign groups", error);
    throw error;
  }
}

export async function FetchCampaignGroupById(groupId, fy) {
  try {
    const { data } = await http.get(`${baseUrl}campaign-groups/${groupId}`, {
      headers: authHeaders(fy),
    });
    return data;
  } catch (error) {
    console.log("Error in fetching campaign group", error);
    throw error;
  }
}

export async function CreateCampaignGroup({ name, campaignIds }) {
  try {
    const { data } = await http.post(
      `${baseUrl}campaign-groups/`,
      { name, campaignIds },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error in creating campaign group", error);
    throw error;
  }
}

export async function AddCampaignsToGroup(groupId, campaignIds) {
  try {
    const { data } = await http.patch(
      `${baseUrl}campaign-groups/${groupId}`,
      { addCampaignIds: campaignIds },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error in adding campaigns to group", error);
    throw error;
  }
}

export async function RemoveCampaignFromGroup(groupId, campaignId) {
  try {
    const { data } = await http.patch(
      `${baseUrl}campaign-groups/${groupId}`,
      { removeCampaignIds: [campaignId] },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error in removing campaign from group", error);
    throw error;
  }
}

export async function DeleteCampaignGroup(groupId) {
  try {
    const { data } = await http.delete(`${baseUrl}campaign-groups/${groupId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error in deleting campaign group", error);
    throw error;
  }
}

// Super-admin-only: record what a brand was actually charged for a campaign
// (and the date that charge was locked in), and fetch the month-wise net
// revenue total across campaigns. Independent of the Filters popover's own
// month filter, which matches campaign *creation* month instead.
export async function AddCampaignRevenue({ campaignId, revenueAmount, revenueLockedDate }) {
  try {
    const { data } = await http.post(
      `${baseUrl}campaign/revenue`,
      { campaignId, revenueAmount, revenueLockedDate },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error saving campaign revenue", error);
    throw error;
  }
}

// Super-admin-only: renames a campaign, and/or adjusts its Budget and/or
// Limit by the given signed amount(s) on top of whatever it's currently set
// to (negative to decrease, positive to increase) — never an overwrite.
export async function AdjustCampaignBudgetLimit({ campaignId, addBudget, addLimit, name }) {
  try {
    const { data } = await http.post(
      `${baseUrl}campaign/budget-limit`,
      { campaignId, addBudget, addLimit, name },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error adjusting campaign budget/limit", error);
    throw error;
  }
}

// Super-admin-only: freezes/unfreezes a campaign — while frozen, it's
// view-only for everyone until any super-admin unfreezes it again.
export async function SetCampaignFrozen({ campaignId, freeze }) {
  try {
    const { data } = await http.post(
      `${baseUrl}campaign/freeze`,
      { campaignId, freeze },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error updating campaign freeze status", error);
    throw error;
  }
}

export async function FetchRevenueSummary(month, year) {
  try {
    const { data } = await http.get(`${baseUrl}campaign/revenue/summary`, {
      params: { month, year },
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching revenue summary", error);
    throw error;
  }
}

// Super-admin-only Reports page, Tab 1: row-level Revenue/Net Revenue/Budget/
// Limit per campaign for the given month, optionally narrowed by POC and a
// campaign/brand name search.
export async function FetchRevenueBudgetReport({ month, year, poc, search }) {
  try {
    const { data } = await http.get(`${baseUrl}campaign/reports/revenue-budget`, {
      params: { month, year, poc, search },
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching revenue & budget report", error);
    throw error;
  }
}

// POC dropdown options for the Revenue & Budget report above.
export async function FetchRevenueBudgetReportFilters() {
  try {
    const { data } = await http.get(`${baseUrl}campaign/reports/filters`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching revenue & budget report filters", error);
    throw error;
  }
}

// Super-admin-only Reports page, Tab 3: month-by-month Revenue/Net Revenue/
// Budget for the Financial Year starting April of fyStartYear.
export async function FetchRevenueTrend(fyStartYear) {
  try {
    const { data } = await http.get(`${baseUrl}campaign/reports/revenue-trend`, {
      params: { year: fyStartYear },
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching revenue trend", error);
    throw error;
  }
}

// Super-admin-only Reports page, Tab 2, primary grid: per-POC summary of
// unique vs. duplicated bank accounts used. Deliberately lightweight (no
// per-link detail) — see FetchDuplicateBankDetailsForPoc below for why.
export async function FetchDuplicateBankDetailsSummary() {
  try {
    const { data } = await http.get(`${baseUrl}status/payment/reports/duplicate-bank-details/summary`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching duplicate bank details summary", error);
    throw error;
  }
}

// Drill-down for one POC row from the summary above: every duplicate
// bank-account group that POC appears in, with full per-link detail
// (including other POCs' links on the same account). Fetched on demand per
// POC rather than once for everyone — on real data, every POC's duplicate
// links combined span tens of thousands of records, too large (and too slow
// to sort server-side) to fetch in one shot.
export async function FetchDuplicateBankDetailsForPoc(pocId) {
  try {
    const { data } = await http.get(`${baseUrl}status/payment/reports/duplicate-bank-details/poc/${pocId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching duplicate bank details for POC", error);
    throw error;
  }
}

// Super-admin-only (the API 403s otherwise): every user who can be given
// access to this campaign, each already flagged with whether they have it.
export async function FetchCampaignShares(campaignId) {
  try {
    const { data } = await http.get(`${baseUrl}campaign/share/${campaignId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching campaign access list", error);
    throw error;
  }
}

// Sends the complete list of users who should have access, not a delta — so
// anyone left out of userIds is removed. Keeps the dialog authoritative
// rather than relying on individual add/remove calls all landing.
export async function UpdateCampaignShares(campaignId, userIds) {
  try {
    const { data } = await http.post(
      `${baseUrl}campaign/share`,
      { campaignId, userIds },
      { headers: authHeaders() },
    );
    return data;
  } catch (error) {
    console.log("Error updating campaign access", error);
    throw error;
  }
}

// The same roster as FetchCampaignShares, for the Add Campaign form — the
// campaign doesn't exist yet there, so nothing can be pre-selected.
export async function FetchShareableUsers() {
  try {
    const { data } = await http.get(`${baseUrl}campaign/share-users`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching shareable users", error);
    throw error;
  }
}

export async function FetchBrandsData() {
  try {
    const endpoint = "brand/";
    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log("Error in Fetching Brands...");
    throw error;
  }
}

// async function FetchCampaignDetailStatus(params) {
//   try {
//     const endpoint = "campaign/detail/status";
//     const { data } = await http.post(`${baseUrl}${endpoint}`,params, {
//       headers: {
//         "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
//         "x-fy": financialYear,
//       },
//     });
//     return data;
//   } catch (error) {
//     console.log("Error in fetching Campaign status");
//     throw error;
//   }
// }
export async function FetchCampaignDetailStatus(params) {
  try {
    const endpoint = "campaign/detail/status";

    const userCookie = GetCookieByName("user");
    const authCookie = GetCookieByName("auth");

    if (!userCookie || !authCookie) {
      throw new Error("Missing authentication cookies");
    }

    const user = JSON.parse(userCookie);
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];

    const requestPayload = {
      campaignId: params.id,
      userId: user._id,
    };

    const { data } = await http.post(`${baseUrl}${endpoint}`, requestPayload, {
      headers: {
        "x-auth-token": token,
        "x-fy": financialYear,
      },
    });

    return data;
  } catch (error) {
    console.log(" Error in fetching Campaign status");
    throw error;
  }
}

async function FetchCampaignDetailsById(params) {
  try {
    const endpoint = "payment/detailbyid";
    const userCookie = GetCookieByName("user");
    const authCookie = GetCookieByName("auth");

    const user = JSON.parse(userCookie);
    const parsedAuth = JSON.parse(authCookie);
    const token = parsedAuth["token"];
    const requestPayload = {
      userId: user._id,
      campaignId: params.id,
    };

    const data = await http.post(`${baseUrl}${endpoint}`, requestPayload, {
      headers: {
        "x-auth-token": token,
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

// Only ever called while browsing with no active filter (any Financial Year
// other than "All" routes through FetchCampaignSearch instead), so this
// always runs under the default "All" header — no fy param to thread here.
async function FetchPendingCampaigns(cursor) {
  const token = JSON.parse(GetCookieByName("auth"))["token"];
  // In case of GET request no need to add token header
  // Its added by middleware if exists
  const headers = {
    "x-fy": financialYear,
    "x-auth-token": token
  };

  let url = `${baseUrl}campaign/fetch-pending`;
  if (cursor) {
    url += `?cursor=${cursor}`;
  }

  const { data } = await http.get(url, { headers: headers });

  return data;
}

// POC dropdown options for the Payment Status page's filter bar — scoped
// server-side to the exact same access rules FetchPaymentStatus's results
// are, so every option offered is guaranteed to actually produce rows.
export async function FetchPaymentStatusPOCs() {
  try {
    const { data } = await http.get(`${baseUrl}status/payment/filters`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.log("Error fetching payment status POC filters", error);
    throw error;
  }
}

export async function FetchPaymentStatus({
  page = 1,
  search = "",
  status = "",
  limit = 100,
  poc = "",
  overdue = false,
  overdueSort = "",
} = {}) {
  try {
    const userCookie = GetCookieByName("user"), authCookie = GetCookieByName("auth");
    const user = JSON.parse(userCookie);
    const token = JSON.parse(authCookie)['token'];

    let searchParams = new URLSearchParams();

    searchParams.append("id", user._id);
    searchParams.append("page", page);

    if (status && status !== "all") {
      searchParams.append("status", status);
    }

    if (search) {
      searchParams.append("search", search);
    }

    if (poc) {
      searchParams.append("poc", poc);
    }

    if (overdue) {
      searchParams.append("overdue", "true");

      if (overdueSort === "asc" || overdueSort === "desc") {
        searchParams.append("overdueSort", overdueSort);
      }
    }

    console.log(`searchparams: `, searchParams.toString());

    const { data } = await http.get(
      baseUrl + `status/payment/fetch-links?${searchParams.toString()}`,
      {
        headers: {
          "x-auth-token": token,
          "x-fy": financialYear
        }
      }
    );

    return data;

    // let searchParam;

    // let queryString = `id=${user._id}&page=${page}`;
    // if (status) {
    //   searchParam = `status=${STATUS_MAP[status]}`;
    // }

    // if (search?.trim()) {
    //   if (searchParam) {
    //     searchParam += `&all=${search.trim()}`;
    //   }
    //   else {
    //     searchParam = `status=&all=${search.trim()}`;
    //   }
    // }

    // if (searchParam) {
    //   queryString += `&search=${encodeURIComponent(searchParam)}`;
    // }

    // const { data } = await http.get(
    //   baseUrl + `status/payment?${queryString}`,
    //   {
    //     headers: {
    //       "x-auth-token": token,
    //       "x-fy": financialYear
    //     }
    //   }
    // );

    // return data;
  }
  catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || "Error in fetching payment links";
    return {
      success: false,
      message: errorMessage
    };
  }
}

// Internal users for the Add Campaign form's Salesperson picker.
export async function FetchSalespersonUsers() {
  try {
    const { data } = await http.get(`${baseUrl}campaign/salesperson-users`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.log("Error fetching salesperson users", error);
    throw error;
  }
}

export async function FindBrandByName(name) {
  const { data } = await http.get(`${baseUrl}brand/findbyname`, {
    params: { name },
    headers: {
      "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
      "x-fy": financialYear,
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function PostBrands(brandData) {
  try {
    const endpoint = "brand/";
    const { data } = await http.post(`${baseUrl}${endpoint}`, brandData, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  }
  catch (error) {
    throw error;
  }
}

export async function FetchVendorCategoriesData() {
  try {
    const endpoint = "vendorcategory/";
    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function FetchVendorPlatforms() {
  try {
    const endpoint = "platform/";
    const { data } = await http.get(`${baseUrl}${endpoint}`, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function PostVendor(vendorData) {
  try {
    const endpoint = "company/addcompany";
    const { data } = await http.post(`${baseUrl}${endpoint}`, vendorData, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

// Create a new Campaign
export async function PostCampaign(formData) {
  try {
    const endpoint = "campaign/";
    const { data } = await http.post(`${baseUrl}${endpoint}`, formData, {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    });
    return data;
  } catch (error) {
    console.log(`Error in Posting Data`, error);
    throw error;
  }
}

async function CalculateVendorPercentage(postdata) {
  const { data } = await http.post(
    baseUrl + `vendor/calculate/percentage`,
    postdata,
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "X-FY": financialYear,
      }
    }
  );

  return data;
}

async function CreateVendorPaymentLink(postdata) {
  const { data } = await http.post(
    baseUrl + `payment/vendor-payment-link`,
    postdata,
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "X-FY": financialYear,
      }
    }
  )

  return data;
}

async function DeletePaymentLink(postdata) {
  const { data } = await http.post(
    baseUrl + `payment/declined`,
    postdata,
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear
      }
    }
  );

  return data;
}

async function ApprovePayment(postdata) {
  const { data } = await http.post(
    baseUrl + `payment/getapproved`,
    postdata,
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear
      }
    }
  );

  return data;
}

// Super-admin-only: moves Waiting payment link(s) into "Under Process" —
// always an array (a single-row action just sends a one-element one).
// Only links that are actually still Waiting get touched server-side, so
// mixing in an ineligible id is harmless, not an error.
async function MarkPaymentsUnderProcess(paymentLinkIds) {
  const { data } = await http.post(
    baseUrl + `payment/mark-under-process`,
    { paymentLinkIds },
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear
      }
    }
  );

  return data;
}

// Super-admin-only: undoes the above, for link(s) actually marked Under
// Process, back to a plain Waiting.
async function RevertPaymentsToWaiting(paymentLinkIds) {
  const { data } = await http.post(
    baseUrl + `payment/revert-to-waiting`,
    { paymentLinkIds },
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear
      }
    }
  );

  return data;
}

async function ViewChildPaymentLinks(postdata) {
  const { data } = await http.post(
    baseUrl + `payment/links`,
    postdata,
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear
      }
    }
  );

  return data;
}

// Attaches a report's already-fetched influencer/post stats directly to a
// campaign's payment queue — no manual link-pasting, no live social-API calls.
// Safe to call repeatedly: content already queued for a creator is left alone,
// only genuinely new content gets added.
export async function AttachReportToCampaign(reportId, campaignId) {
  const { data } = await http.post(
    baseUrl + `payment/bulk-generate/from-report`,
    { reportId, campaignId },
    {
      headers: {
        "x-auth-token": JSON.parse(GetCookieByName("auth"))["token"],
        "x-fy": financialYear,
      },
    },
  );
  return data;
}

export default {
  paymentCampaign,
  latestPaymentCampaign,
  FetchVendorCategories,
  FetchVendors,
  FetchVendorSearch,
  FetchCampaigns,
  FetchCampaignSearch,
  FetchCampaignFilters,
  FetchCampaignGroups,
  FetchCampaignGroupById,
  CreateCampaignGroup,
  AddCampaignsToGroup,
  RemoveCampaignFromGroup,
  DeleteCampaignGroup,
  FetchBrandsData,
  FetchCampaignDetailStatus,
  FetchCampaignDetailsById,
  FetchPendingCampaigns,
  FetchPaymentStatus,
  FetchPaymentStatusPOCs,
  PostBrands,
  FindBrandByName,
  FetchVendorCategoriesData,
  FetchVendorPlatforms,
  PostVendor,
  PostCampaign,
  CalculateVendorPercentage,
  CreateVendorPaymentLink,
  DeletePaymentLink,
  ApprovePayment,
  MarkPaymentsUnderProcess,
  RevertPaymentsToWaiting,
  FetchAllCampaigns,
  ViewChildPaymentLinks,
  AttachReportToCampaign,
  AddCampaignRevenue,
  AdjustCampaignBudgetLimit,
  SetCampaignFrozen,
  FetchRevenueSummary,
  FetchRevenueBudgetReport,
  FetchRevenueBudgetReportFilters,
  FetchRevenueTrend,
  FetchDuplicateBankDetailsSummary,
  FetchDuplicateBankDetailsForPoc,
  FetchCampaignShares,
  UpdateCampaignShares,
  FetchShareableUsers,
  FetchSalespersonUsers,
};
